/**
 * Chat / BYOH Chat History API
 * Routes the per-mission BYOH message history proxy via governance-service.
 *
 * F17 (chat-ui catch-up): the chatHistoryApi surface (sessions CRUD /
 * messages list / archive / unarchive) was removed alongside the backend
 * chat_session_handler.go. The chat_sessions table was never written to in
 * the mission-centric model; conversation history surfaces via
 * useConversationHistory (reads /v1/executions). Only the BYOH per-mission
 * messages proxy survives — chat history within an active mission still
 * comes from the Edge host.
 */

import { apiClient, ApiClient } from './api-client'
import { operatorTakeoverEnabled } from '../utils/config'

// =============================================================================
// BYOH CHAT HISTORY API
// Routes chat history requests to BYOH hosts via VPN gateway
// Used when session.hostType === 'byoh'
// =============================================================================

// BYOH session response matches SDK format
export interface BYOHChatSessionResponse {
  id: string
  organization_id: string
  agent_id: string
  host_id: string
  mission_id: string | null
  user_id: string | null
  title: string | null
  status: string
  message_count: number
  total_tokens: number
  created_at: string
  updated_at: string
}

export interface BYOHSessionListResponse {
  sessions: BYOHChatSessionResponse[]
  total: number
  limit: number
  offset: number
}

// BYOH message response matches SDK format
export interface BYOHChatMessageResponse {
  id: string
  session_id: string
  mission_id?: string | null
  agent_id?: string | null
  user_id?: string | null
  host_id?: string | null
  role: string // 'user' | 'assistant' | 'system' | 'tool'
  content: string
  tool_call_id: string | null
  tool_calls: unknown[] | null
  model_id: string | null
  tokens_input: number | null
  tokens_output: number | null
  latency_ms: number | null
  finish_reason: string | null
  trust_tier: number | null
  trace_id: string | null
  agent_name: string | null
  user_name: string | null
  created_at: string
  /**
   * "operator" for a reply a person wrote in an operator takeover
   * (agion/Agion#3435), returned only to a read with include_operator=true,
   * with the hold and the person's id and display name.
   */
  author_kind?: string
  hold_id?: string
  operator_user_id?: string
  operator_display_name?: string
}

export interface BYOHMessageListResponse {
  messages: BYOHChatMessageResponse[]
  total: number
  session_id: string
}

/**
 * BYOH Proxy API - Routes chat history requests to BYOH hosts via chat-service proxy
 * The proxy resolves host location and routes through VPN gateway internally
 * This avoids the browser trying to call internal K8s services directly
 *
 * Simplified API: mission_id is the primary filter (no session concept)
 */
export const byohProxyApi = {
  /**
   * Get all messages for a mission from BYOH host via chat-service proxy (host-based routing)
   * This is the preferred endpoint - uses host_id directly
   * @param hostId - BYOH host ID
   * @param missionId - Mission ID to filter messages
   * @param limit - Max messages to return
   * @param agentId - Optional agent ID to filter messages for agent-specific chat history
   * @param executionId - The run whose conversation is read. The server reads
   *   only that run's session and refuses a run whose content the reader may
   *   not read (403 conversation_not_viewable, agion/Agion#3019).
   *   With operator takeover on it also asks for the replies people wrote
   *   in a takeover (include_operator, agion/Agion#3435).
   */
  getMissionMessagesByHost: async (
    hostId: string,
    missionId: string,
    limit: number = 500,
    agentId?: string,
    sessionId?: string,
    executionId?: string
  ): Promise<BYOHMessageListResponse> => {
    const params: Record<string, string> = {
      limit: limit.toString(),
    }
    if (executionId) {
      params.execution_id = executionId
    }
    if (agentId) {
      params.agent_id = agentId
    }
    if (sessionId) {
      params.session_id = sessionId
    }
    if (operatorTakeoverEnabled()) {
      params.include_operator = 'true'
    }
    return apiClient.get<BYOHMessageListResponse>(
      `/v1/chat/byoh/hosts/${hostId}/missions/${missionId}/messages`,
      params
    )
  },

  /**
   * Get agent-specific messages from a BYOH mission
   * Convenience wrapper for getMissionMessagesByHost with agent filtering
   * @param hostId - BYOH host ID
   * @param missionId - Mission ID
   * @param agentId - Agent ID to filter messages
   * @param limit - Max messages to return
   */
  getAgentMessagesByHost: async (
    hostId: string,
    missionId: string,
    agentId: string,
    limit: number = 100
  ): Promise<BYOHMessageListResponse> => {
    return byohProxyApi.getMissionMessagesByHost(hostId, missionId, limit, agentId)
  },

  /**
   * @deprecated Use getMissionMessagesByHost instead - agent routing is legacy
   * Get all messages for a mission from BYOH host via chat-service proxy
   * @param agentId - Agent ID (used to resolve BYOH host location)
   * @param missionId - Mission ID to filter messages
   * @param limit - Max messages to return
   */
  getMissionMessages: async (
    agentId: string,
    missionId: string,
    limit: number = 100
  ): Promise<BYOHMessageListResponse> => {
    return apiClient.get<BYOHMessageListResponse>(
      `/v1/chat/byoh/agents/${agentId}/missions/${missionId}/messages`,
      {
        limit: limit.toString(),
      }
    )
  },
}

// Legacy: Direct BYOH API client (only works if browser can reach the host)
// DEPRECATED: Use byohProxyApi instead - browser cannot call VPN gateway directly
export const createBYOHChatApi = (hostEndpoint: string) => {
  const byohClient = new ApiClient(hostEndpoint)

  return {
    /**
     * Get chat sessions from BYOH host
     * @deprecated Use byohProxyApi.getSessions() instead
     */
    getSessions: async (
      userId: string,
      organizationId: string,
      missionId?: string,
      limit: number = 50,
      offset: number = 0
    ): Promise<BYOHSessionListResponse> => {
      const params: Record<string, string> = {
        user_id: userId,
        organization_id: organizationId,
        limit: limit.toString(),
        offset: offset.toString(),
      }
      if (missionId) {
        params.mission_id = missionId
      }
      return byohClient.get<BYOHSessionListResponse>('/v1/chat/sessions', params)
    },

    /**
     * Get messages for a session from BYOH host
     * @deprecated Use byohProxyApi.getSessionMessages() instead
     */
    getSessionMessages: async (
      sessionId: string,
      limit: number = 100,
      includeSystem: boolean = true
    ): Promise<BYOHMessageListResponse> => {
      return byohClient.get<BYOHMessageListResponse>(
        `/v1/chat/sessions/${sessionId}/messages`,
        {
          limit: limit.toString(),
          include_system: includeSystem.toString(),
        }
      )
    },
  }
}
